// Admin-only queue inspector. Shows the dispatcher's per-state row
// counts plus the age of the oldest pending row — a leading
// indicator that the dispatcher isn't keeping up.

import { useState } from 'react';
import { Activity, AlertTriangle, Layers } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { runBackfill, useQueueStats, type QueueStats } from '../../api/detection';

type Tone = 'success' | 'warning' | 'danger' | 'accent' | 'neutral';

function fmtSec(s: number): string {
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h`;
  return `${Math.floor(s / 86400)} d`;
}

function oldestPendingTone(q: QueueStats | undefined): Tone {
  if (!q) return 'neutral';
  if (q.oldest_pending_sec < 60) return 'success';
  if (q.oldest_pending_sec < 600) return 'warning';
  return 'danger';
}

function oldestCardClassName(tone: Tone): string {
  if (tone === 'danger') return 'border-danger/40 bg-danger/5';
  if (tone === 'warning') return 'border-warning/40 bg-warning/5';
  return 'border-success/40 bg-success/5';
}

function oldestBadgeTone(tone: Tone): 'danger' | 'warning' | 'success' {
  if (tone === 'danger') return 'danger';
  if (tone === 'warning') return 'warning';
  return 'success';
}

function tileColour(tone: Tone): string {
  if (tone === 'success') return 'text-success';
  if (tone === 'warning') return 'text-warning';
  if (tone === 'danger') return 'text-danger';
  if (tone === 'accent') return 'text-accent';
  return 'text-fg';
}

export function DetectionQueue() {
  const { data: q, isLoading, error, mutate } = useQueueStats();
  const [busy, setBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState<string | null>(null);

  const onBackfill = async () => {
    if (!confirm('Enqueue every (artifact, enabled-scanner) pair that doesn\'t already have a job? On a busy registry this can be tens of millions of rows. The job runs at backlog priority and drains in the background.')) {
      return;
    }
    setBusy(true);
    setBackfillResult(null);
    try {
      const r = await runBackfill();
      setBackfillResult(`Enqueued ${r.enqueued.toLocaleString()} backlog jobs.`);
      await mutate();
    } catch (e) {
      setBackfillResult(`Failed: ${e}`);
    } finally {
      setBusy(false);
    }
  };

  const oldestTone = oldestPendingTone(q);

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <p className="orb-eyebrow text-accent/80 mb-2">Detection · admin</p>
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-accent/10 border border-accent/30 flex items-center justify-center shrink-0">
              <Activity className="h-5 w-5 text-accent" />
            </div>
            <div>
              <h1 className="text-2xl font-display font-semibold text-fg leading-tight">Scan queue</h1>
              <p className="text-fg-muted text-sm mt-2 max-w-2xl">
                Live counts from the scan_jobs table. Refreshes every 10 s.
                Growing pending depth or aged oldest-pending row means the
                dispatcher pool isn't draining work fast enough — add
                scanner pods or reduce scanner cost.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <Card className="border-danger/30 bg-danger/5">
            <CardBody>
              <p className="text-danger font-mono text-sm">{String(error)}</p>
            </CardBody>
          </Card>
        )}

        <Card>
          <CardHeader
            title="Backfill"
            description="Enqueue scan jobs for every artifact in the registry that doesn't already have one. Runs at priority 9 (backlog) so live work is unaffected."
            actions={
              <Button variant="secondary" disabled={busy} onClick={onBackfill}>
                <Layers className="h-4 w-4" />
                {busy ? 'Enqueueing…' : 'Enqueue backlog'}
              </Button>
            }
          />
          {backfillResult && (
            <CardBody>
              <p className="text-sm text-fg">{backfillResult}</p>
            </CardBody>
          )}
        </Card>


        {isLoading && (
          <Card><CardBody><p className="text-fg-muted">Loading…</p></CardBody></Card>
        )}
        {!isLoading && q && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <Tile label="Pending" value={q.pending} tone={q.pending > 1000 ? 'warning' : 'neutral'} />
              <Tile label="Claimed" value={q.claimed} tone="neutral" />
              <Tile label="Running" value={q.running} tone="accent" />
              <Tile label="Done" value={q.done} tone="success" />
              <Tile label="Failed" value={q.failed} tone={q.failed > 0 ? 'danger' : 'neutral'} />
              <Tile label="Skipped" value={q.skipped} tone="neutral" />
            </div>

            <Card className={oldestCardClassName(oldestTone)}>
              <CardHeader
                title="Oldest pending"
                description="How long the front of the queue has been waiting. < 60 s is healthy."
                actions={<Badge tone={oldestBadgeTone(oldestTone)}>{fmtSec(q.oldest_pending_sec)}</Badge>}
              />
              {oldestTone !== 'success' && (
                <CardBody>
                  <div className="flex items-start gap-2 text-sm text-fg-muted">
                    <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
                    <span>
                      Dispatcher is behind. Check pod count and scanner cost-class
                      mix; consider raising worker pool size in the dispatcher
                      config.
                    </span>
                  </div>
                </CardBody>
              )}
            </Card>
          </>
        )}
      </div>
    </AppShell>
  );
}

function Tile({ label, value, tone }: Readonly<{ label: string; value: number; tone: Tone }>) {
  const colour = tileColour(tone);
  return (
    <Card>
      <CardBody>
        <p className="orb-section-title text-fg-muted">{label}</p>
        <p className={`text-3xl font-display font-semibold ${colour} tabular-nums mt-1.5`}>
          {value.toLocaleString()}
        </p>
      </CardBody>
    </Card>
  );
}
